import {
  Button,
  DatePicker,
  Dropdown,
  ErrorContainer,
  LabelComponent,
  LoadingSpinner,
  Stack
} from 'components';
import { TextAreaInputField } from 'components/inputField/textAreaInpuField';
import dayjs from 'dayjs';
import { setIn, useFormik } from 'formik';
import debounce from 'lodash.debounce';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  getOtherScreenerDetails,
  getOtherScreenerHistory,
  getScreeners,
  patchOtherScreener,
  putOtherScreener
} from 'store/screeener/screenerActions';
import * as yup from 'yup';

import UpdatedLast from '../components/updatedLast';
import style from './other.module.scss';
import OtherFooter from './otherFooter';

type Values = {
  dateOfScreening: string;
  needsIdentified: string[];
  notes: string;
};

type Props = {
  closeModal: () => void;
};

const Other = ({ closeModal }: Props) => {
  const { clientId = '' } = useParams();
  const dispatch = useAppDispatch();
  const otherScreneer = useAppSelector((state) => state.screener[clientId]?.otherScreener);
  const [isAutosaving, setIsAutosaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<null | string>(null);

  const needsIdentifiedData = [
    {
      id: 'ACCESS_TO_HEALTHCARE',
      name: 'Access to healthcare',
      info: 'Access to healthcare providers, equipment, supplies and medications',
      show: false
    },
    {
      id: 'ACTIVITIES_OF_DAILY_LIVING',
      name: 'Activities of daily living & disability',
      info: 'Taking care of oneself, having access to disability services and benefits',
      show: true
    },
    {
      id: 'BENEFITS_INSURANCE',
      name: 'Benefits & insurance',
      info: 'Health insurance, SNAP, and other public benefits',
      show: true
    },
    {
      id: 'CAREGIVING',
      name: 'Caregiving',
      info: 'Caregiver responsibilities',
      show: true
    },
    {
      id: 'EDUCATION_LITERACY',
      name: 'Education & literacy',
      info: 'Connection to educational resources (like a GED program), health literacy',
      show: true
    },
    {
      id: 'EMPLOYMENT',
      name: 'Employment',
      info: 'Having enough paid work',
      show: true
    },
    {
      id: 'EMPOWERMENT',
      name: 'Empowerment',
      info: 'Discrimination, lack of neighborhood resources, political empowerment',
      show: true
    },
    {
      id: 'FINANCIAL_STABILITY',
      name: 'Financial stability',
      info: 'Income, making ends meet',
      show: true
    },
    {
      id: 'HEALTH_HABITS',
      name: 'Health habits',
      info: 'Physical activity, diet, sleep, medication adherence',
      show: true
    },
    {
      id: 'INCARCERATION',
      name: 'Incarceration',
      info: 'Personal experience being in prison or jail',
      show: true
    },
    {
      id: 'INTIMATE_PARTNER_SAFETY',
      name: 'Intimate partner safety',
      info: 'Feeling safe in close relationships',
      show: true
    },
    {
      id: 'LEGAL',
      name: 'Legal',
      info: 'Legal services e.g. immigration, tenant rights',
      show: true
    },
    {
      id: 'MENTAL_HEALTH',
      name: 'Mental health',
      info: 'Mood, depression, anxiety, grief',
      show: true
    },
    {
      id: 'PHYSICAL_HEALTH',
      name: 'Physical health',
      info: 'Self-rated physical health and chronic conditions',
      show: false
    },
    {
      id: 'PHYSICAL_HEALTH_AND_ACCESS_TO_HEALTHCARE',
      name: 'Physical health and Access to healthcare',
      info: 'Self-rated physical health and chronic conditions; access to healthcare providers, equipment, supplies, and medications',
      show: true
    },
    {
      id: 'CHILDCARE',
      name: 'Childcare',
      category: 'resources',
      show: true
    },
    {
      id: 'CLOTHING_GOODS',
      name: 'Clothing & goods',
      category: 'resources',
      show: true
    },
    {
      id: 'FOOD',
      name: 'Food',
      category: 'resources',
      show: true
    },
    {
      id: 'HOUSING',
      name: 'Housing',
      category: 'resources',
      show: true
    },
    {
      id: 'PHONE_INTERNET',
      name: 'Phone & internet',
      category: 'resources',
      show: true
    },
    {
      id: 'TRANSPORTATION',
      name: 'Transportation',
      category: 'resources',
      show: true
    },
    {
      id: 'UTILITIES',
      name: 'Utilities',
      category: 'resources',
      show: true
    },
    {
      id: 'SOCIAL_SUPPORT',
      name: 'Social support',
      info: 'Social and community connections, loneliness',
      show: true
    },
    {
      id: 'SUBSTANCE_USE',
      name: 'Substance use',
      info: 'Use of alcohol, tobacco, drugs',
      show: true
    }
  ].filter((item) => item.show);

  const formik = useFormik({
    initialValues: {
      dateOfScreening: otherScreneer?.dateOfScreening,
      needsIdentified: otherScreneer?.needsIdentified ?? [],
      notes: otherScreneer?.notes ?? ''
    },
    validationSchema: yup.object().shape({
      dateOfScreening: yup
        .string()
        .nullable()
        .test('dateOfScreening', 'Date cannot be in the future.', (value) => {
          if (!value) {
            return true;
          }
          const now = dayjs();

          const isInFuture = dayjs(value).isAfter(now, 'day');

          return !isInFuture;
        })
    }),
    onSubmit: async (values) => {
      const response = await dispatch(
        patchOtherScreener({
          clientId,
          type: 'other',
          screener: { ...values, submitted: true }
        })
      );
      if (response.meta.requestStatus === 'fulfilled') {
        dispatch(
          getOtherScreenerHistory({
            clientId
          })
        );
        dispatch(
          getOtherScreenerDetails({
            clientId
          })
        );
        closeModal();
      }
    }
  });

  const autosaveForm = async (values: Values) => {
    setIsAutosaving(true);
    const response = await dispatch(
      putOtherScreener({ clientId, type: 'other', screener: values })
    );
    setIsAutosaving(false);
    if (response.meta.requestStatus === 'fulfilled') {
      setLastSaved(new Date().toISOString());
    }
    dispatch(getScreeners({ clientId }));
    dispatch(getOtherScreenerDetails({ clientId }));
  };

  const debounceFn = useCallback(debounce(autosaveForm, 900), []);

  const handleChange = async ({ name, value }: { name: string; value: unknown }) => {
    formik.handleChange({ target: { name, value } });

    debounceFn(setIn(formik.values, name, value));
  };

  const isEmpty = [
    formik.values.dateOfScreening,
    formik.values.needsIdentified,
    formik.values.notes
  ].every((value) => value == null || value === '' || value?.length === 0);

  const loading = formik.isSubmitting || isAutosaving;
  const submitDisabled = isEmpty || loading || otherScreneer?.submitted || !formik.isValid;

  return (
    <form style={{ height: '100%' }} onSubmit={formik.handleSubmit}>
      <Stack height="100%">
        <Stack pb="24px" minHeight={0} height="100%">
          <Stack display="flex" gap="16px" height="100%" overflow="auto" minHeight={0}>
            <p className={style.title}>Did you complete a different screener? Capture it here!</p>
            <Stack display="flex" gap="4px">
              <Stack>
                <LabelComponent text="Date of Screening" />
                <DatePicker
                  value={formik.values.dateOfScreening}
                  onChange={(date: string) =>
                    handleChange({ name: 'dateOfScreening', value: date })
                  }
                  disableFuture
                  onBlur={() =>
                    formik.setFieldTouched('dateOfScreening', true).then(() => {
                      formik.validateForm();
                    })
                  }
                />
                <ErrorContainer
                  errors={formik.errors}
                  visible={formik.errors.dateOfScreening && formik.touched.dateOfScreening}
                  name="dateOfScreening"
                />
              </Stack>
              <Stack>
                <LabelComponent text="Needs Identified" />
                <Dropdown
                  label="Select"
                  name="needsIdentified"
                  data={needsIdentifiedData}
                  value={formik.values.needsIdentified}
                  onChange={(e) => handleChange({ name: 'needsIdentified', value: e.target.value })}
                  onBlur={formik.handleBlur}
                  categoryList={[{ id: 'resources', label: 'Resources' }]}
                  categorySelectable
                  multiSelect
                  enableSorting
                />
                <ErrorContainer errors={formik.errors} visible={false} name="" />
              </Stack>
              <Stack>
                <LabelComponent text="Notes" />
                <TextAreaInputField
                  onBlur={formik.handleBlur}
                  onChange={(e) => handleChange({ name: 'notes', value: e.target.value })}
                  rowsNumber={4}
                  placeholder="Type here"
                  value={formik.values.notes}
                />
                <ErrorContainer errors={formik.errors} visible={false} name="" />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <OtherFooter>
          <Stack display="flex" flexDirection="row" alignItems="center" gap="16px">
            {loading ? (
              <LoadingSpinner size={30} />
            ) : (
              <UpdatedLast lastSaved={lastSaved} updatedAt={otherScreneer?.updatedAt} />
            )}
            <Stack width="125px">
              <Button disabled={submitDisabled} text="Submit" theme="primary" />
            </Stack>
          </Stack>
        </OtherFooter>
      </Stack>
    </form>
  );
};

export default Other;
